import { type FormEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { ApiError, apiRequest } from '../lib/api.js';
import { readJson, useRemote } from '../lib/remote.js';
import { timezoneChoices } from '../lib/timezones.js';
import { EmailTemplatesPanel } from './EmailTemplatesPanel.js';
import { parseTablePageSizeOptions, refreshAdminUiPrefs, useUiPrefs } from '../lib/ui-prefs.js';
import { Hint } from '../components/ui-legacy/Hint.js';
import {
  Button,
  Card,
  Checkbox,
  ColorField,
  Field,
  FileField,
  Icon,
  type IconName,
  PageHeader,
  Select,
  TextInput,
  Textarea,
} from '../components/ui/index.js';
import { useAdminLocale } from '../lib/admin-locale.js';

interface SmtpSettings {
  host: string | null;
  port: number | null;
  user: string | null;
  mailFrom: string | null;
  passwordSet: boolean;
  source: 'platform' | 'env' | 'none';
}

interface PlatformSettings {
  provider: { kind: 'mock' | 'openai'; model: string | null; keyConfigured: boolean };
  smtp: SmtpSettings;
}

type SettingValueType = 'TEXT' | 'INT' | 'FLOAT' | 'BOOL' | 'JSON';

interface ApplicationSettingItem {
  key: string;
  valueType: SettingValueType;
  category: string;
  description: string;
  value: string;
  defaultValue: string;
  isDefault: boolean;
  isActive: boolean;
  updatedAt: string | null;
}

type SectionId =
  'provider' | 'smtp' | 'ai' | 'limits' | 'security' | 'locales' | 'branding' | 'ops' | 'mail';

const NAV: ReadonlyArray<{
  id: SectionId;
  label: string;
  labelEn: string;
  description: string;
  descriptionEn: string;
  icon: IconName;
  kind: 'core' | 'app';
  category?: string;
}> = [
  {
    id: 'provider',
    label: 'Πάροχος AI',
    labelEn: 'AI provider',
    description: 'Mock ή OpenAI',
    descriptionEn: 'Mock or OpenAI',
    icon: 'pulse',
    kind: 'core',
  },
  {
    id: 'smtp',
    label: 'SMTP',
    labelEn: 'SMTP',
    description: 'Αποστολή email',
    descriptionEn: 'Email delivery',
    icon: 'mail',
    kind: 'core',
  },
  {
    id: 'ai',
    label: 'AI / RAG',
    labelEn: 'AI / RAG',
    description: 'Retrieval & chunking',
    descriptionEn: 'Retrieval & chunking',
    icon: 'layers',
    kind: 'app',
    category: 'ai',
  },
  {
    id: 'limits',
    label: 'Όρια',
    labelEn: 'Limits',
    description: 'PDF, logo, quotas',
    descriptionEn: 'PDF, logo, quotas',
    icon: 'chart',
    kind: 'app',
    category: 'limits',
  },
  {
    id: 'security',
    label: 'Ασφάλεια',
    labelEn: 'Security',
    description: 'TTL & rate limits',
    descriptionEn: 'TTL & rate limits',
    icon: 'shield',
    kind: 'app',
    category: 'security',
  },
  {
    id: 'locales',
    label: 'Γλώσσες / ζώνη',
    labelEn: 'Languages / time zone',
    description: 'Defaults πλατφόρμας',
    descriptionEn: 'Platform defaults',
    icon: 'globe',
    kind: 'app',
    category: 'locales',
  },
  {
    id: 'branding',
    label: 'Εμφάνιση',
    labelEn: 'Appearance',
    description: 'White label',
    descriptionEn: 'White label',
    icon: 'eye',
    kind: 'app',
    category: 'branding',
  },
  {
    id: 'ops',
    label: 'Λειτουργία',
    labelEn: 'Operations',
    description: 'Retention & plugin',
    descriptionEn: 'Retention & plugin',
    icon: 'server',
    kind: 'app',
    category: 'ops',
  },
  {
    id: 'mail',
    label: 'Κείμενα email',
    labelEn: 'Email copy',
    description: 'Πρότυπα text / HTML',
    descriptionEn: 'Text / HTML templates',
    icon: 'file',
    kind: 'app',
    category: 'mail',
  },
];

function isSectionId(value: string | null): value is SectionId {
  return NAV.some((item) => item.id === value);
}

type LocalText = (el: string, en: string) => string;

function saveError(error: unknown, text: LocalText): string {
  const message = error instanceof ApiError ? error.message : '';
  if (message.includes('production')) {
    return text(
      'Σε production ο δοκιμαστικός πάροχος δεν επιτρέπεται.',
      'The mock provider is not allowed in production.',
    );
  }
  if (message.includes('key or chat model')) {
    return text(
      'Ο διακομιστής δεν έχει κλειδί ή μοντέλο OpenAI. Το κλειδί δεν γράφεται εδώ.',
      'The server has no OpenAI key or model. The key is not entered here.',
    );
  }
  return text('Η αποθήκευση δεν ολοκληρώθηκε.', 'The save operation did not complete.');
}

/** Stays under the app header while the settings content scrolls. */
function SettingsStickyBar(props: {
  title: string;
  description?: string;
  notice?: { tone: 'ok' | 'error'; text: string } | null;
  actions: ReactNode;
}) {
  return (
    <div className="sticky top-0 z-[6] mb-4 border-b border-[color:var(--admin-border)] bg-[color:var(--admin-paper)]/95 py-3 backdrop-blur">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {props.title}
          </h2>
          {props.description ? (
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{props.description}</p>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-3">{props.actions}</div>
      </div>
      {props.notice ? (
        <p
          className={
            props.notice.tone === 'ok'
              ? 'mt-2 text-sm text-emerald-700 dark:text-emerald-300'
              : 'mt-2 text-sm text-rose-700 dark:text-rose-300'
          }
        >
          {props.notice.text}
        </p>
      ) : null}
    </div>
  );
}

function ProviderForm(props: { settings: PlatformSettings; onSaved: () => void }) {
  const { text } = useAdminLocale();
  const [kind, setKind] = useState(props.settings.provider.kind);
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const ready = props.settings.provider.keyConfigured && Boolean(props.settings.provider.model);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setNotice(null);
    try {
      await readJson(
        '/v1/platform/settings',
        { method: 'PATCH', body: JSON.stringify({ provider: kind }) },
        null,
      );
      setNotice({
        tone: 'ok',
        text: text('Ο πάροχος αποθηκεύτηκε.', 'The provider was saved.'),
      });
      props.onSaved();
    } catch (error) {
      setNotice({ tone: 'error', text: saveError(error, text) });
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={(event) => void onSubmit(event)}>
      <SettingsStickyBar
        title={text('Πάροχος AI', 'AI provider')}
        description={text(
          'Η επιλογή ισχύει για όλους τους πελάτες. Το κλειδί μένει στον διακομιστή και δεν εμφανίζεται.',
          'The selection applies to all customers. The key remains on the server and is never displayed.',
        )}
        notice={notice}
        actions={
          <Button type="submit" disabled={pending}>
            <Icon name="save" className="size-4" />
            {text('Αποθήκευση', 'Save')}
          </Button>
        }
      />
      <Card title={text('Ρυθμίσεις παρόχου', 'Provider settings')}>
        <Field label={text('Πάροχος', 'Provider')}>
          <Select
            className="mt-1.5"
            value={kind}
            onChange={(next) => setKind(next === 'openai' ? 'openai' : 'mock')}
            options={[
              {
                value: 'mock',
                label: text('Mock — δοκιμή, χωρίς χρέωση', 'Mock — testing, no charge'),
              },
              { value: 'openai', label: 'OpenAI' },
            ]}
          />
        </Field>
        <dl className="mt-4 space-y-2 text-sm text-slate-700 dark:text-slate-300">
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500 dark:text-slate-400">{text('Μοντέλο', 'Model')}</dt>
            <dd>{props.settings.provider.model ?? text('Δεν έχει οριστεί', 'Not set')}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-slate-500 dark:text-slate-400">
              {text('Κλειδί διακομιστή', 'Server key')}
            </dt>
            <dd>
              {props.settings.provider.keyConfigured
                ? text('Ορισμένο στον διακομιστή', 'Configured on the server')
                : text('Δεν έχει οριστεί', 'Not set')}
            </dd>
          </div>
        </dl>
        {kind === 'openai' ? (
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
            {ready
              ? text(
                  'Οι επόμενες απαντήσεις και οι νέες ενσωματώσεις χρησιμοποιούν το κλειδί του διακομιστή και μπορεί να έχουν χρέωση. Οι πηγές που έχουν ήδη ενσωματωθεί δεν ξαναδιαβάζονται μόνες τους.',
                  'Future answers and new embeddings use the server key and may incur charges. Sources that are already embedded are not processed again automatically.',
                )
              : text(
                  'Ο διακομιστής δεν έχει ακόμη κλειδί ή μοντέλο. Η επιλογή OpenAI δεν αποθηκεύεται ώσπου να οριστούν εκεί. Το κλειδί δεν γράφεται σε αυτή τη φόρμα.',
                  'The server does not have a key or model yet. OpenAI cannot be saved until both are configured there. The key is not entered in this form.',
                )}
          </p>
        ) : (
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
            {text(
              'Οι απαντήσεις παράγονται τοπικά και το εκτιμώμενο κόστος μένει μηδέν.',
              'Answers are generated locally and estimated cost remains zero.',
            )}
          </p>
        )}
      </Card>
    </form>
  );
}

function SmtpForm(props: { settings: PlatformSettings; onSaved: () => void }) {
  const { text } = useAdminLocale();
  const smtp = props.settings.smtp;
  const [host, setHost] = useState(smtp.host ?? '');
  const [port, setPort] = useState(String(smtp.port ?? 587));
  const [user, setUser] = useState(smtp.user ?? '');
  const [password, setPassword] = useState('');
  const [clearPassword, setClearPassword] = useState(false);
  const [mailFrom, setMailFrom] = useState(smtp.mailFrom ?? '');
  const [testTo, setTestTo] = useState('');
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setPending(true);
    setNotice(null);
    try {
      await readJson(
        '/v1/platform/settings/smtp',
        {
          method: 'PATCH',
          body: JSON.stringify({
            host: host.trim(),
            port: Number.parseInt(port, 10),
            user: user.trim() || null,
            mailFrom: mailFrom.trim(),
            ...(clearPassword ? { clearPassword: true } : password ? { password } : {}),
          }),
        },
        null,
      );
      setPassword('');
      setClearPassword(false);
      setNotice({
        tone: 'ok',
        text: text('Οι ρυθμίσεις SMTP αποθηκεύτηκαν.', 'SMTP settings were saved.'),
      });
      props.onSaved();
    } catch (error) {
      setNotice({
        tone: 'error',
        text:
          error instanceof ApiError
            ? error.message
            : text('Η αποθήκευση SMTP απέτυχε.', 'Saving SMTP settings failed.'),
      });
    } finally {
      setPending(false);
    }
  }

  async function onTest(): Promise<void> {
    setPending(true);
    setNotice(null);
    try {
      await readJson(
        '/v1/platform/settings/smtp/test',
        { method: 'POST', body: JSON.stringify({ to: testTo.trim() }) },
        null,
      );
      setNotice({
        tone: 'ok',
        text: text(
          'Το δοκιμαστικό μήνυμα στάλθηκε (ή γράφτηκε στο τοπικό sink).',
          'The test message was sent (or written to the local sink).',
        ),
      });
    } catch (error) {
      setNotice({
        tone: 'error',
        text:
          error instanceof ApiError
            ? error.message
            : text('Η δοκιμαστική αποστολή απέτυχε.', 'Sending the test message failed.'),
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={(event) => void onSubmit(event)} className="space-y-4">
      <SettingsStickyBar
        title="SMTP"
        description={text(
          'Προσκλήσεις, επαναφορά κωδικού και ειδοποιήσεις. Ο κωδικός δεν εμφανίζεται μετά την αποθήκευση.',
          'Invitations, password resets, and notifications. The password is not shown after saving.',
        )}
        notice={notice}
        actions={
          <Button type="submit" disabled={pending}>
            <Icon name="save" className="size-4" />
            {text('Αποθήκευση SMTP', 'Save SMTP')}
          </Button>
        }
      />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={text('Διακομιστής αλληλογραφίας', 'Mail server')}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Host">
              <TextInput required value={host} onChange={(event) => setHost(event.target.value)} />
            </Field>
            <Field label="Port">
              <TextInput
                required
                type="number"
                min={1}
                max={65535}
                value={port}
                onChange={(event) => setPort(event.target.value)}
              />
            </Field>
            <Field
              label={text('Χρήστης', 'Username')}
              hint={text(
                'Κενό αν ο διακομιστής δεν ζητά αυθεντικοποίηση.',
                'Leave blank if the server does not require authentication.',
              )}
            >
              <TextInput value={user} onChange={(event) => setUser(event.target.value)} />
            </Field>
            <Field label={text('Αποστολέας (From)', 'Sender (From)')}>
              <TextInput
                required
                type="email"
                value={mailFrom}
                onChange={(event) => setMailFrom(event.target.value)}
              />
            </Field>
            <Field
              label={text('Κωδικός', 'Password')}
              hint={
                smtp.passwordSet
                  ? text(
                      'Έχει οριστεί. Αφήστε κενό για να μείνει ίδιος.',
                      'Configured. Leave blank to keep it unchanged.',
                    )
                  : text('Προαιρετικό αν δεν απαιτείται.', 'Optional if not required.')
              }
            >
              <TextInput
                type="password"
                autoComplete="new-password"
                value={password}
                disabled={clearPassword}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>
            <Checkbox
              className="mt-6"
              checked={clearPassword}
              onChange={(event) => {
                setClearPassword(event.target.checked);
                if (event.target.checked) setPassword('');
              }}
              label={text('Διαγραφή αποθηκευμένου κωδικού', 'Delete saved password')}
            />
          </div>
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
            {text('Τρέχουσα πηγή:', 'Current source:')}{' '}
            {smtp.source === 'platform'
              ? text('ρυθμίσεις πλατφόρμας', 'platform settings')
              : smtp.source === 'env'
                ? text('μεταβλητές περιβάλλοντος (.env)', 'environment variables (.env)')
                : text(
                    'καμία — τοπικά χρησιμοποιείται το dev mail sink',
                    'none — the development mail sink is used locally',
                  )}
          </p>
        </Card>
        <Card
          title={text('Δοκιμαστική αποστολή', 'Test delivery')}
          description={text(
            'Στείλτε μόνο στη δική σας διεύθυνση.',
            'Send only to your own address.',
          )}
        >
          <Field label={text('Παραλήπτης', 'Recipient')}>
            <TextInput
              type="email"
              value={testTo}
              onChange={(event) => setTestTo(event.target.value)}
            />
          </Field>
          <div className="mt-4">
            <Button
              type="button"
              variant="secondary"
              disabled={pending || !testTo.trim()}
              onClick={() => void onTest()}
            >
              <Icon name="mail" className="size-4" />
              {text('Αποστολή δοκιμής', 'Send test')}
            </Button>
          </div>
        </Card>
      </div>
    </form>
  );
}

const BRAND_COLOR_KEYS = new Set([
  'admin_accent_color',
  'admin_selection_bg',
  'admin_selection_text',
  'admin_sidebar_bg_light',
  'admin_sidebar_text_light',
  'admin_sidebar_active_bg_light',
  'admin_sidebar_active_text_light',
  'admin_text_color',
  'admin_sidebar_bg',
  'admin_sidebar_text',
  'admin_sidebar_active_bg',
  'admin_sidebar_active_text',
  'admin_text_color_dark',
]);

/** Branding settings grouped for clear General / Light / Dark sections. */
const BRANDING_GROUPS: ReadonlyArray<{
  id: string;
  title: string;
  titleEn: string;
  description: string;
  descriptionEn: string;
  keys: readonly string[];
}> = [
  {
    id: 'identity',
    title: 'Ταυτότητα',
    titleEn: 'Identity',
    description: 'Κείμενα και ονόματα — ισχύουν και στα δύο θέματα.',
    descriptionEn: 'Text and names — applies to both themes.',
    keys: [
      'admin_product_name',
      'admin_login_subtitle',
      'admin_auth_headline',
      'admin_auth_body',
      'admin_font_family',
      'admin_logo_url',
      'admin_favicon_url',
      'plugin_menu_name',
      'plugin_page_title',
    ],
  },
  {
    id: 'general',
    title: 'Γενικά χρώματα',
    titleEn: 'General colors',
    description: 'Ίδια και στο Light και στο Dark.',
    descriptionEn: 'The same in Light and Dark themes.',
    keys: ['admin_accent_color', 'admin_selection_bg', 'admin_selection_text'],
  },
  {
    id: 'light',
    title: 'Light Theme',
    titleEn: 'Light Theme',
    description: 'Μόνο όταν είναι ενεργό το φωτεινό θέμα.',
    descriptionEn: 'Only when the light theme is active.',
    keys: [
      'admin_sidebar_bg_light',
      'admin_sidebar_text_light',
      'admin_sidebar_active_bg_light',
      'admin_sidebar_active_text_light',
      'admin_text_color',
    ],
  },
  {
    id: 'dark',
    title: 'Dark Theme',
    titleEn: 'Dark Theme',
    description: 'Μόνο όταν είναι ενεργό το σκουρό θέμα.',
    descriptionEn: 'Only when the dark theme is active.',
    keys: [
      'admin_sidebar_bg',
      'admin_sidebar_text',
      'admin_sidebar_active_bg',
      'admin_sidebar_active_text',
      'admin_text_color_dark',
    ],
  },
];

const SETTING_LABELS: Record<string, string> = {
  admin_product_name: 'Όνομα προϊόντος',
  admin_login_subtitle: 'Υπότιτλος σύνδεσης',
  admin_auth_headline: 'Τίτλος πάνελ σύνδεσης',
  admin_auth_body: 'Κείμενο πάνελ σύνδεσης',
  admin_accent_color: 'Έμφαση (κουμπιά, σύνδεσμοι)',
  admin_selection_bg: 'Επιλογές — φόντο',
  admin_selection_text: 'Επιλογές — κείμενο',
  admin_sidebar_bg_light: 'Sidebar — φόντο',
  admin_sidebar_text_light: 'Sidebar — κείμενο',
  admin_sidebar_active_bg_light: 'Sidebar — επιλεγμένο φόντο',
  admin_sidebar_active_text_light: 'Sidebar — επιλεγμένο κείμενο',
  admin_text_color: 'Κείμενο σελίδας',
  admin_sidebar_bg: 'Sidebar — φόντο',
  admin_sidebar_text: 'Sidebar — κείμενο',
  admin_sidebar_active_bg: 'Sidebar — επιλεγμένο φόντο',
  admin_sidebar_active_text: 'Sidebar — επιλεγμένο κείμενο',
  admin_text_color_dark: 'Κείμενο σελίδας',
  admin_font_family: 'Γραμματοσειρά',
  admin_logo_url: 'Logo (HTTPS URL)',
  admin_favicon_url: 'Favicon (HTTPS URL)',
  plugin_menu_name: 'Μενού WordPress',
  plugin_page_title: 'Τίτλος σελίδας WordPress',
  show_answer_sources: 'Εμφάνιση πηγών στην απάντηση',
  show_answer_feedback: 'Χρήσιμη / μη χρήσιμη απάντηση',
};

const SETTING_LABELS_EN: Record<string, string> = {
  admin_product_name: 'Product name',
  admin_login_subtitle: 'Sign-in subtitle',
  admin_auth_headline: 'Sign-in panel title',
  admin_auth_body: 'Sign-in panel text',
  admin_accent_color: 'Accent (buttons, links)',
  admin_selection_bg: 'Selections — background',
  admin_selection_text: 'Selections — text',
  admin_sidebar_bg_light: 'Sidebar — background',
  admin_sidebar_text_light: 'Sidebar — text',
  admin_sidebar_active_bg_light: 'Sidebar — selected background',
  admin_sidebar_active_text_light: 'Sidebar — selected text',
  admin_text_color: 'Page text',
  admin_sidebar_bg: 'Sidebar — background',
  admin_sidebar_text: 'Sidebar — text',
  admin_sidebar_active_bg: 'Sidebar — selected background',
  admin_sidebar_active_text: 'Sidebar — selected text',
  admin_text_color_dark: 'Page text',
  admin_font_family: 'Font',
  admin_logo_url: 'Logo (HTTPS URL)',
  admin_favicon_url: 'Favicon (HTTPS URL)',
  plugin_menu_name: 'WordPress menu',
  plugin_page_title: 'WordPress page title',
  show_answer_sources: 'Show sources in answers',
  show_answer_feedback: 'Helpful / unhelpful answer',
};

type ThemePreset = {
  label: string;
  description: string;
  descriptionEn: string;
  values: Record<string, string>;
};

const THEME_PRESETS: Record<string, ThemePreset> = {
  signal_ink: {
    label: 'Signal Ink',
    description: 'Ink sidebar, paper canvas, μπλε signal accent.',
    descriptionEn: 'Ink sidebar, paper canvas, blue signal accent.',
    values: {
      admin_accent_color: '#2563EB',
      admin_selection_bg: '#2563EB',
      admin_selection_text: '#FFFFFF',
      admin_sidebar_bg_light: '#0B1220',
      admin_sidebar_text_light: '#94A3B8',
      admin_sidebar_active_bg_light: '#2563EB',
      admin_sidebar_active_text_light: '#FFFFFF',
      admin_text_color: '#0F172A',
      admin_sidebar_bg: '#0B1220',
      admin_sidebar_text: '#94A3B8',
      admin_sidebar_active_bg: '#2563EB',
      admin_sidebar_active_text: '#FFFFFF',
      admin_text_color_dark: '#E2E8F0',
      admin_font_family: 'ibm_plex',
    },
  },
  graphite_signal: {
    label: 'Graphite / Orange',
    description: 'Σκούρο graphite chrome με ζεστό operational accent.',
    descriptionEn: 'Dark graphite chrome with a warm operational accent.',
    values: {
      admin_accent_color: '#F97316',
      admin_selection_bg: '#F97316',
      admin_selection_text: '#FFFFFF',
      admin_sidebar_bg_light: '#171717',
      admin_sidebar_text_light: '#A3A3A3',
      admin_sidebar_active_bg_light: '#F97316',
      admin_sidebar_active_text_light: '#FFFFFF',
      admin_text_color: '#171717',
      admin_sidebar_bg: '#0A0A0A',
      admin_sidebar_text: '#A3A3A3',
      admin_sidebar_active_bg: '#F97316',
      admin_sidebar_active_text: '#FFFFFF',
      admin_text_color_dark: '#F5F5F5',
      admin_font_family: 'ibm_plex',
    },
  },
  paper_teal: {
    label: 'Paper / Teal',
    description: 'Φωτεινό paper workspace με καθαρό teal navigation.',
    descriptionEn: 'Bright paper workspace with clean teal navigation.',
    values: {
      admin_accent_color: '#0F766E',
      admin_selection_bg: '#0F766E',
      admin_selection_text: '#FFFFFF',
      admin_sidebar_bg_light: '#F8FAFC',
      admin_sidebar_text_light: '#475569',
      admin_sidebar_active_bg_light: '#0F766E',
      admin_sidebar_active_text_light: '#FFFFFF',
      admin_text_color: '#0F172A',
      admin_sidebar_bg: '#0F172A',
      admin_sidebar_text: '#CBD5E1',
      admin_sidebar_active_bg: '#0F766E',
      admin_sidebar_active_text: '#FFFFFF',
      admin_text_color_dark: '#E2E8F0',
      admin_font_family: 'ibm_plex',
    },
  },
  midnight_lime: {
    label: 'Midnight / Lime',
    description: 'Deep navy λειτουργικό περιβάλλον με sharp lime signal.',
    descriptionEn: 'Deep navy operational workspace with a sharp lime signal.',
    values: {
      admin_accent_color: '#A3E635',
      admin_selection_bg: '#A3E635',
      admin_selection_text: '#17210B',
      admin_sidebar_bg_light: '#101827',
      admin_sidebar_text_light: '#A7B3C7',
      admin_sidebar_active_bg_light: '#A3E635',
      admin_sidebar_active_text_light: '#17210B',
      admin_text_color: '#172033',
      admin_sidebar_bg: '#070B12',
      admin_sidebar_text: '#9AA8BC',
      admin_sidebar_active_bg: '#A3E635',
      admin_sidebar_active_text: '#17210B',
      admin_text_color_dark: '#E7EDF5',
      admin_font_family: 'ibm_plex',
    },
  },
  monochrome_red: {
    label: 'Monochrome / Red',
    description: 'Ασπρόμαυρη command-center βάση με κόκκινο alert accent.',
    descriptionEn: 'Monochrome command-center base with a red alert accent.',
    values: {
      admin_accent_color: '#DC2626',
      admin_selection_bg: '#DC2626',
      admin_selection_text: '#FFFFFF',
      admin_sidebar_bg_light: '#111111',
      admin_sidebar_text_light: '#A3A3A3',
      admin_sidebar_active_bg_light: '#DC2626',
      admin_sidebar_active_text_light: '#FFFFFF',
      admin_text_color: '#171717',
      admin_sidebar_bg: '#050505',
      admin_sidebar_text: '#A3A3A3',
      admin_sidebar_active_bg: '#DC2626',
      admin_sidebar_active_text: '#FFFFFF',
      admin_text_color_dark: '#F5F5F5',
      admin_font_family: 'mono',
    },
  },
  ocean_cyan: {
    label: 'Ocean / Cyan',
    description: 'Cool blue-black chrome με καθαρό cyan interaction layer.',
    descriptionEn: 'Cool blue-black chrome with a clean cyan interaction layer.',
    values: {
      admin_accent_color: '#06B6D4',
      admin_selection_bg: '#0891B2',
      admin_selection_text: '#FFFFFF',
      admin_sidebar_bg_light: '#082F49',
      admin_sidebar_text_light: '#A5F3FC',
      admin_sidebar_active_bg_light: '#06B6D4',
      admin_sidebar_active_text_light: '#083344',
      admin_text_color: '#0F172A',
      admin_sidebar_bg: '#061826',
      admin_sidebar_text: '#A5F3FC',
      admin_sidebar_active_bg: '#06B6D4',
      admin_sidebar_active_text: '#083344',
      admin_text_color_dark: '#E0F2FE',
      admin_font_family: 'ibm_plex',
    },
  },
};

const AI_SETTING_HINTS: Record<string, string> = {
  openai_embedding_model:
    'Το μοντέλο που μετατρέπει το κείμενο των σελίδων και την ερώτηση του επισκέπτη σε διανύσματα, ώστε να συγκρίνονται μεταξύ τους. Η προεπιλογή είναι text-embedding-3-small, στις 1536 διαστάσεις της βάσης. Η αλλαγή δεν ξαναδιαβάζει τις σελίδες που είναι ήδη έτοιμες: τα παλιά διανύσματα μένουν όπως είναι, ώσπου να ξανασυγχρονιστεί η πηγή. Μοντέλο με άλλες διαστάσεις δεν ταιριάζει με τα αποθηκευμένα αποσπάσματα και η αναζήτηση αποτυγχάνει.',
  openai_request_timeout_ms:
    'Πόσα χιλιοστά του δευτερολέπτου περιμένει ο διακομιστής μια κλήση προς το OpenAI, είτε για ενσωμάτωση σελίδας είτε για απάντηση. Η προεπιλογή είναι 20000 (20 δευτερόλεπτα). Μικρότερη τιμή κόβει αργές αλλά έγκυρες απαντήσεις και ο επισκέπτης βλέπει ότι η υπηρεσία δεν είναι διαθέσιμη. Μεγαλύτερη τιμή τον αφήνει να περιμένει περισσότερο όταν το OpenAI έχει κολλήσει. Αν η κλήση έχει ήδη φύγει και λήξει ο χρόνος, το κόστος μπορεί να έχει χρεωθεί χωρίς να εμφανιστεί απάντηση.',
  evidence_min_similarity:
    'Το κατώτερο όριο ομοιότητας (cosine) για να σταλεί ένα απόσπασμα στο μοντέλο. Η προεπιλογή είναι 0.25. Κάτω από αυτό το όριο το απόσπασμα αγνοείται και, αν δεν μείνει κανένα, ο επισκέπτης βλέπει το μήνυμα ότι δεν υπάρχουν αρκετές πληροφορίες. Χαμηλότερη τιμή αφήνει πιο ελεύθερες, σύντομες ερωτήσεις να φτάνουν στις σελίδες. Υψηλότερη τιμή κρατάει μόνο τα πολύ κοντινά αποσπάσματα και το ίδιο μήνυμα εμφανίζεται συχνότερα. Οι κοινές λέξεις δεν αντικαθιστούν αυτό το όριο, εκτός αν καλύπτουν τουλάχιστον το 55% των λέξεων της ερώτησης. Ισχύει στην επόμενη απάντηση, χωρίς νέο συγχρονισμό.',
  grounding_top_k:
    'Πόσα αποσπάσματα, το πολύ, δίνονται στο μοντέλο αφού περάσουν το όριο ομοιότητας. Η προεπιλογή είναι 8. Από την ίδια σελίδα κρατούνται έως 3 αποσπάσματα, ώστε μια μεγάλη σελίδα να μην σκεπάζει όλες τις άλλες. Μεγαλύτερος αριθμός δίνει πιο πλήρη συμφραζόμενα και αυξάνει τα tokens της κλήσης. Μικρότερος αριθμός κάνει την απάντηση στενότερη και μπορεί να μείνει έξω ένα στοιχείο που ήταν στο τέταρτο ή πέμπτο απόσπασμα.',
  context_token_budget:
    'Πόσα tokens κειμένου, περίπου, χωράνε συνολικά τα αποσπάσματα που στέλνονται στο μοντέλο. Η προεπιλογή είναι 1800. Μόλις γεμίσει το όριο, δεν προστίθεται άλλο απόσπασμα. Το πρώτο απόσπασμα μπαίνει ακόμη κι αν από μόνο του είναι μεγαλύτερο. Δεν περιορίζει το μήκος της απάντησης. Αυτό ορίζεται από το πακέτο του πελάτη. Μεγαλύτερο όριο βοηθάει σε μακροσκελείς σελίδες και κοστίζει περισσότερο ανά ερώτηση.',
  history_turns:
    'Πόσες προηγούμενες ανταλλαγές της ίδιας συνομιλίας στέλνονται μαζί με τη νέα ερώτηση. Η προεπιλογή είναι 6. Έτσι μια συνέχεια όπως «και το τηλέφωνο;» μένει δεμένη με την προηγούμενη ερώτηση. Περισσότερες ανταλλαγές καταναλώνουν περισσότερα tokens. Δεν περιλαμβάνει συνομιλίες άλλων επισκεπτών.',
  history_chars_per_turn:
    'Σε πόσους χαρακτήρες κόβεται κάθε παλιό μήνυμα πριν μπει στο ιστορικό. Η προεπιλογή είναι 400. Ένα πολύ μεγάλο προηγούμενο μήνυμα δεν γεμίζει όλο το διαθέσιμο πλαίσιο. Η τρέχουσα ερώτηση δεν κόβεται από αυτή τη ρύθμιση.',
  chunk_target_tokens:
    'Σε πόσα tokens κόβεται περίπου το κείμενο μιας σελίδας όταν ενσωματώνεται. Η προεπιλογή είναι 500. Μικρότερα κομμάτια ταιριάζουν καλύτερα σε σύντομες ερωτήσεις, αλλά δημιουργούν περισσότερα διανύσματα. Μεγαλύτερα κομμάτια κρατούν περισσότερη πρόταση μαζί, όμως η ομοιότητα μιας σύντομης ερώτησης πέφτει γιατί το απόσπασμα μιλάει για πολλά θέματα. Ισχύει στον επόμενο συγχρονισμό. Οι σελίδες που είναι ήδη έτοιμες δεν ξανακόβονται μόνες τους.',
  chunk_overlap_tokens:
    'Πόσα tokens επαναλαμβάνονται στο όριο δύο διαδοχικών κομματιών. Η προεπιλογή είναι 80. Έτσι μια πρόταση που πέφτει ακριβώς στην τομή δεν χάνεται ολόκληρη από το ένα κομμάτι. Μεγάλη επικάλυψη διπλασιάζει κείμενο και αυξάνει το κόστος ενσωμάτωσης. Μηδέν κόβει καθαρά, με κίνδυνο να κοπεί μια πρόταση στη μέση. Ισχύει μόνο στην επόμενη ενσωμάτωση.',
  retrieval_vector_limit:
    'Πόσα κοντινότερα αποσπάσματα διαβάζονται από τη βάση πριν εφαρμοστούν το όριο ομοιότητας και το μέγιστο πλήθος που πάει στο μοντέλο. Η προεπιλογή είναι 24. Αν η σχετική σελίδα δεν είναι μέσα σε αυτά, δεν φτάνει ποτέ στην απάντηση, όσο σωστή κι αν είναι. Μεγαλύτερη τιμή κοιτάζει μεγαλύτερο μέρος της βάσης και επιβαρύνει λίγο την αναζήτηση. Δεν αυξάνει μόνη της τα tokens του OpenAI. Αυξάνονται μόνο όσα αποσπάσματα περάσουν μετά το όριο.',
  ingestion_job_attempts:
    'Πόσες φορές ξαναδοκιμάζει ο worker μια αποτυχημένη ενσωμάτωση. Η προεπιλογή είναι 5, με αυξανόμενη αναμονή που ξεκινά από 2 δευτερόλεπτα. Καλύπτει προσωρινό σφάλμα του OpenAI ή της βάσης. Όταν εξαντληθούν οι προσπάθειες, η έκδοση της πηγής μένει σε αποτυχία και δεν γίνεται ενεργή. Επιτυχημένη σελίδα δεν ξανατρέχει εξαιτίας αυτού του αριθμού.',
  show_answer_sources:
    'Όταν είναι ενεργό, ο επισκέπτης βλέπει κάτω από την απάντηση τις σελίδες από τις οποίες βγήκε, με τίτλο και σύνδεσμο αν η σελίδα είναι δημόσια. Όταν είναι ανενεργό, το widget δείχνει μόνο το κείμενο. Οι πηγές συνεχίζουν να αποθηκεύονται και φαίνονται στο ιστορικό της συνομιλίας μέσα στη διαχείριση. Ισχύει στην επόμενη απάντηση, για όλα τα site.',
  show_answer_feedback:
    'Όταν είναι ενεργό, ο επισκέπτης βλέπει κάτω από κάθε απάντηση τα κουμπιά «Χρήσιμη απάντηση» και «Μη χρήσιμη απάντηση». Όταν είναι ανενεργό, τα κουμπιά δεν εμφανίζονται και νέα αξιολόγηση δεν καταγράφεται. Οι αξιολογήσεις που έχουν ήδη δοθεί μένουν στο ιστορικό της διαχείρισης. Ισχύει στο επόμενο άνοιγμα του widget, για όλα τα site.',
};

const AI_SETTING_HINTS_EN: Record<string, string> = {
  openai_embedding_model:
    'The model that converts page text and visitor questions into vectors for comparison. The default is text-embedding-3-small with the database’s 1,536 dimensions. Changing it does not reprocess existing pages; old vectors remain until the source is synchronized again. A model with different dimensions is incompatible with stored chunks and causes search to fail.',
  openai_request_timeout_ms:
    'How many milliseconds the server waits for an OpenAI call, for either page embedding or an answer. The default is 20000 (20 seconds). A lower value interrupts slow but valid responses; a higher value makes visitors wait longer when OpenAI is stalled. A timed-out call may still incur a charge.',
  evidence_min_similarity:
    'The minimum cosine similarity required to send a chunk to the model. The default is 0.25. Chunks below it are ignored; if none remain, the visitor sees the insufficient-information response. Lower values accept broader questions, while higher values retain only closer matches. Applies to the next answer without resynchronization.',
  grounding_top_k:
    'The maximum number of qualifying chunks sent to the model. The default is 8, with up to 3 chunks from the same page. Higher values provide broader context and use more tokens; lower values narrow the answer and may omit relevant details.',
  context_token_budget:
    'The approximate total token budget for chunks sent to the model. The default is 1800. No more chunks are added after the budget is filled, although the first chunk is included even if it is larger. This does not limit answer length. Higher values help with long pages and cost more per question.',
  history_turns:
    'The number of previous exchanges from the same conversation sent with a new question. The default is 6. This keeps follow-up questions connected to prior context. More exchanges consume more tokens and never include other visitors’ conversations.',
  history_chars_per_turn:
    'The maximum characters retained from each old message before it enters conversation history. The default is 400. This prevents one long message from filling the context. It does not truncate the current question.',
  chunk_target_tokens:
    'The approximate token size used when splitting a page for embedding. The default is 500. Smaller chunks match short questions better but create more vectors; larger chunks preserve more context but can reduce similarity. Applies on the next synchronization.',
  chunk_overlap_tokens:
    'The number of tokens repeated where consecutive chunks meet. The default is 80. Overlap keeps sentences at a boundary from being lost, but increases duplicated text and embedding cost. Applies only to the next embedding.',
  retrieval_vector_limit:
    'The number of nearest chunks read from the database before similarity and top-count filters are applied. The default is 24. A larger value searches more of the database but does not itself increase OpenAI tokens.',
  ingestion_job_attempts:
    'The number of times the worker retries a failed embedding. The default is 5 with increasing delays starting at 2 seconds. After all attempts fail, the source version remains failed and does not become active.',
  show_answer_sources:
    'When enabled, visitors see the source pages below an answer, including a title and link for public pages. When disabled, only answer text is shown. Sources are still stored and remain visible in administration history. Applies to the next answer on every site.',
  show_answer_feedback:
    'When enabled, visitors see Helpful and Not helpful buttons below each answer. When disabled, the buttons are hidden and no new feedback is recorded. Existing feedback remains in administration history. Applies the next time the widget opens on every site.',
};

function SettingInfo(props: { label: string; content: string }) {
  const { text } = useAdminLocale();
  return (
    <Hint content={props.content} trigger="click" side="bottom" maxWidth={380} label={props.label}>
      <button
        type="button"
        className="inline-flex size-5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        aria-label={text(`Επεξήγηση: ${props.label}`, `Explanation: ${props.label}`)}
      >
        <Icon name="info" className="size-3.5" />
      </button>
    </Hint>
  );
}

function BrandFilesCard() {
  const { text } = useAdminLocale();
  const { logoUrl, faviconUrl, logoUploaded, faviconUploaded } = useUiPrefs();
  const [pending, setPending] = useState<'logo' | 'favicon' | null>(null);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  async function upload(kind: 'logo' | 'favicon', file: File | null): Promise<void> {
    if (!file) return;
    setPending(kind);
    setNotice(null);
    try {
      const response = await apiRequest(`/v1/platform/brand/${kind}`, {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: await file.arrayBuffer(),
      });
      if (!response.ok) {
        const body: unknown = await response.json().catch(() => null);
        const message =
          body && typeof body === 'object' && 'error' in body
            ? String((body as { error?: { message?: string } }).error?.message ?? response.status)
            : `HTTP ${response.status}`;
        throw new ApiError(response.status, message);
      }
      refreshAdminUiPrefs();
      setNotice({
        tone: 'ok',
        text:
          kind === 'logo'
            ? text('Το logo αποθηκεύτηκε.', 'The logo was saved.')
            : text('Το favicon αποθηκεύτηκε.', 'The favicon was saved.'),
      });
    } catch (error) {
      setNotice({
        tone: 'error',
        text:
          error instanceof ApiError
            ? error.message
            : text('Η μεταφόρτωση απέτυχε.', 'The upload failed.'),
      });
    } finally {
      setPending(null);
    }
  }

  async function clear(kind: 'logo' | 'favicon'): Promise<void> {
    setPending(kind);
    setNotice(null);
    try {
      const response = await apiRequest(`/v1/platform/brand/${kind}`, { method: 'DELETE' });
      if (!response.ok && response.status !== 204) {
        throw new ApiError(response.status, text('Η αφαίρεση απέτυχε.', 'The removal failed.'));
      }
      refreshAdminUiPrefs();
      setNotice({
        tone: 'ok',
        text: text(
          'Το αρχείο αφαιρέθηκε. Ισχύει το URL, αν υπάρχει.',
          'The file was removed. The URL applies if one is set.',
        ),
      });
    } catch (error) {
      setNotice({
        tone: 'error',
        text:
          error instanceof ApiError
            ? error.message
            : text('Η αφαίρεση απέτυχε.', 'The removal failed.'),
      });
    } finally {
      setPending(null);
    }
  }

  return (
    <Card
      title={text('Αρχεία μάρκας', 'Brand files')}
      description={text(
        'PNG, JPEG ή WebP. Το αρχείο υπερισχύει του HTTPS URL.',
        'PNG, JPEG, or WebP. An uploaded file takes precedence over the HTTPS URL.',
      )}
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Logo</p>
          {logoUploaded && logoUrl ? (
            <img
              src={logoUrl}
              alt=""
              className="mt-2 h-12 w-12 rounded-lg object-contain bg-slate-100"
            />
          ) : (
            <p className="mt-2 text-xs text-slate-500">
              {text('Δεν έχει ανέβει αρχείο.', 'No file has been uploaded.')}
            </p>
          )}
          <FileField
            className="mt-3"
            accept="image/png,image/jpeg,image/webp"
            disabled={pending !== null}
            label={text('Ανέβασμα logo', 'Upload logo')}
            onChange={(file) => void upload('logo', file)}
          />
          {logoUploaded ? (
            <Button
              type="button"
              variant="ghost"
              className="mt-2"
              disabled={pending !== null}
              onClick={() => void clear('logo')}
            >
              {text('Αφαίρεση logo', 'Remove logo')}
            </Button>
          ) : null}
        </div>
        <div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">Favicon</p>
          {faviconUploaded && faviconUrl ? (
            <img
              src={faviconUrl}
              alt=""
              className="mt-2 h-8 w-8 rounded object-contain bg-slate-100"
            />
          ) : (
            <p className="mt-2 text-xs text-slate-500">
              {text('Δεν έχει ανέβει αρχείο.', 'No file has been uploaded.')}
            </p>
          )}
          <FileField
            className="mt-3"
            accept="image/png,image/jpeg,image/webp"
            disabled={pending !== null}
            label={text('Ανέβασμα favicon', 'Upload favicon')}
            onChange={(file) => void upload('favicon', file)}
          />
          {faviconUploaded ? (
            <Button
              type="button"
              variant="ghost"
              className="mt-2"
              disabled={pending !== null}
              onClick={() => void clear('favicon')}
            >
              {text('Αφαίρεση favicon', 'Remove favicon')}
            </Button>
          ) : null}
        </div>
      </div>
      {notice ? (
        <p
          className={
            notice.tone === 'ok'
              ? 'mt-4 text-sm text-emerald-700 dark:text-emerald-300'
              : 'mt-4 text-sm text-rose-700 dark:text-rose-300'
          }
        >
          {notice.text}
        </p>
      ) : null}
    </Card>
  );
}

function ThemeStyleCard(props: {
  items: ApplicationSettingItem[];
  draft: Record<string, string>;
  setDraft: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
}) {
  const { text } = useAdminLocale();
  const [preset, setPreset] = useState('signal_ink');
  const selected = THEME_PRESETS[preset] ?? THEME_PRESETS.signal_ink!;

  function applyPreset(nextPreset: string): void {
    const next = THEME_PRESETS[nextPreset];
    if (!next) return;
    setPreset(nextPreset);
    props.setDraft((prev) => {
      const draft = { ...prev };
      for (const [key, value] of Object.entries(next.values)) {
        const current = props.items.find((item) => item.key === key)?.value;
        if (current === value) delete draft[key];
        else draft[key] = value;
      }
      return draft;
    });
  }

  return (
    <Card
      className="overflow-hidden"
      title="Theme style"
      description={text(
        'Εφάρμοσε ένα συνεκτικό visual direction σε όλες τις μεταβλητές εμφάνισης του tab. Οι αλλαγές μένουν ως draft μέχρι την Αποθήκευση.',
        'Apply a consistent visual direction to every appearance variable in this tab. Changes remain a draft until you save.',
      )}
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,17rem)_minmax(0,1fr)]">
        <div className="flex flex-col justify-between gap-5">
          <Field
            label={text('Preset εμφάνισης', 'Appearance preset')}
            hint={text(selected.description, selected.descriptionEn)}
          >
            <Select
              className="mt-1.5"
              value={preset}
              onChange={applyPreset}
              options={Object.entries(THEME_PRESETS).map(([value, theme]) => ({
                value,
                label: theme.label,
              }))}
            />
          </Field>
          <div className="flex items-center gap-2">
            {[
              selected.values.admin_accent_color,
              selected.values.admin_sidebar_bg,
              selected.values.admin_sidebar_text,
              selected.values.admin_text_color,
            ].map((color, index) => (
              <span
                key={`${color}-${index}`}
                className="size-7 rounded-[var(--admin-radius-sm)] border border-black/10 shadow-sm"
                style={{ background: color }}
                title={color}
              />
            ))}
            <span className="admin-mono ml-1 text-[10px] tracking-[0.08em] text-slate-400 uppercase">
              {Object.keys(selected.values).length} tokens
            </span>
          </div>
          <p className="admin-mono text-[10px] leading-relaxed tracking-[0.08em] text-slate-400 uppercase">
            Sidebar · accent · selected states · typography · light/dark
          </p>
        </div>
        <div
          className="overflow-hidden rounded-[var(--admin-radius)] border border-slate-800 shadow-[0_12px_30px_-18px_rgb(15_23_42_/_0.6)]"
          style={{ background: '#F4F6F8' }}
        >
          <div className="grid min-h-36 grid-cols-[5.5rem_1fr]">
            <div
              className="relative flex flex-col gap-2 p-3"
              style={{
                background: selected.values.admin_sidebar_bg,
                color: selected.values.admin_sidebar_text,
              }}
            >
              <span
                className="mb-2 grid size-6 place-items-center rounded-[4px] text-[10px] font-semibold"
                style={{
                  background: selected.values.admin_accent_color,
                  color: selected.values.admin_selection_text,
                }}
              >
                A
              </span>
              <span className="h-1 w-12 rounded-full bg-current opacity-40" />
              <span
                className="h-5 w-full rounded-[4px]"
                style={{
                  background: selected.values.admin_sidebar_active_bg,
                  color: selected.values.admin_sidebar_active_text,
                }}
              />
              <span className="h-1 w-10 rounded-full bg-current opacity-30" />
              <span className="h-1 w-8 rounded-full bg-current opacity-30" />
              <span
                className="absolute right-0 top-0 h-full w-0.5"
                style={{ background: selected.values.admin_accent_color }}
              />
            </div>
            <div className="flex flex-col gap-3 p-3">
              <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                <span className="h-2 w-24 rounded-full bg-slate-400/50" />
                <span
                  className="h-4 w-12 rounded-[3px]"
                  style={{ background: selected.values.admin_accent_color }}
                />
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[0, 1, 2].map((item) => (
                  <span
                    key={item}
                    className="h-14 rounded-[5px] border border-slate-200 bg-white"
                  />
                ))}
              </div>
              <span className="h-2 w-32 rounded-full bg-slate-400/40" />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

function SettingField(props: {
  item: ApplicationSettingItem;
  value: string;
  onChange: (value: string) => void;
  onReset: () => void;
  pageSizeOptions?: number[];
}) {
  const { text } = useAdminLocale();
  const { item, value } = props;
  const changed = value !== item.value;
  const explanation = AI_SETTING_HINTS[item.key];
  const label = text(SETTING_LABELS[item.key] ?? item.key, SETTING_LABELS_EN[item.key] ?? item.key);
  const pageSizeOptions = props.pageSizeOptions ?? [10, 20, 50, 100];
  const pageSizeSelectValue = pageSizeOptions.includes(Number(value))
    ? value
    : String(pageSizeOptions[0] ?? 20);
  return (
    <Field
      label={label}
      labelAddon={
        explanation ? (
          <SettingInfo
            label={label}
            content={text(explanation, AI_SETTING_HINTS_EN[item.key] ?? explanation)}
          />
        ) : undefined
      }
      hint={`${item.description}${item.isDefault && !changed ? text(' · προεπιλογή', ' · default') : ''}`}
    >
      {item.valueType === 'BOOL' ? (
        <Checkbox
          className="mt-2"
          checked={value === 'true'}
          onChange={(event) => props.onChange(event.target.checked ? 'true' : 'false')}
          label={value === 'true' ? text('Ενεργό', 'Enabled') : text('Ανενεργό', 'Disabled')}
        />
      ) : item.key === 'admin_table_page_size' ? (
        <Select
          className="mt-1.5 max-w-xs"
          aria-label={text('Γραμμές ανά σελίδα στους πίνακες', 'Rows per page in tables')}
          value={pageSizeSelectValue}
          onChange={props.onChange}
          options={pageSizeOptions.map((size) => ({
            value: String(size),
            label: text(`${size} γραμμές`, `${size} rows`),
          }))}
        />
      ) : item.key === 'default_timezone' ? (
        <Select
          className="mt-1.5 max-w-sm"
          aria-label={text('Προεπιλεγμένη ζώνη ώρας', 'Default time zone')}
          value={value}
          onChange={props.onChange}
          options={timezoneChoices(value).map((zone) => ({
            value: zone,
            label: zone,
          }))}
        />
      ) : item.key === 'default_locale' ? (
        <Select
          className="mt-1.5 max-w-xs"
          aria-label={text('Προεπιλεγμένη γλώσσα', 'Default language')}
          value={value}
          onChange={props.onChange}
          options={[
            { value: 'el', label: text('Ελληνικά (el)', 'Greek (el)') },
            { value: 'en', label: 'English (en)' },
            { value: 'fr', label: 'Français (fr)' },
          ]}
        />
      ) : BRAND_COLOR_KEYS.has(item.key) ? (
        <ColorField
          className="mt-1.5"
          value={/^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#2563EB'}
          onChange={props.onChange}
          aria-label={label}
        />
      ) : item.key === 'admin_font_family' ? (
        <Select
          className="mt-1.5 max-w-sm"
          aria-label={text('Γραμματοσειρά', 'Font')}
          value={
            ['ibm_plex', 'inter', 'system', 'serif', 'mono'].includes(value) ? value : 'ibm_plex'
          }
          onChange={props.onChange}
          options={[
            { value: 'ibm_plex', label: 'IBM Plex Sans' },
            { value: 'inter', label: 'Inter' },
            { value: 'system', label: 'System' },
            { value: 'serif', label: 'Serif' },
            { value: 'mono', label: 'Mono' },
          ]}
        />
      ) : item.valueType === 'JSON' ||
        item.key.includes('body') ||
        item.key === 'admin_login_subtitle' ||
        item.key === 'admin_auth_headline' ||
        item.key === 'admin_auth_body' ||
        item.value.length > 80 ? (
        <Textarea
          className="mt-1.5 font-mono text-xs"
          rows={3}
          value={value}
          onChange={(event) => props.onChange(event.target.value)}
        />
      ) : (
        <TextInput
          className="mt-1.5 font-mono text-sm"
          type={item.valueType === 'INT' || item.valueType === 'FLOAT' ? 'number' : 'text'}
          step={item.valueType === 'FLOAT' ? 'any' : undefined}
          value={value}
          onChange={(event) => props.onChange(event.target.value)}
        />
      )}
      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
        <span>{item.valueType}</span>
        {changed ? (
          <span className="text-amber-700 dark:text-amber-300">{text('αλλαγμένο', 'changed')}</span>
        ) : null}
        <button
          type="button"
          className="underline-offset-2 hover:underline"
          onClick={props.onReset}
        >
          {text('Επαναφορά προεπιλογής', 'Reset to default')}
        </button>
      </div>
    </Field>
  );
}

function ApplicationCategoryPanel(props: {
  category: string;
  label: string;
  description: string;
  items: ApplicationSettingItem[];
  draft: Record<string, string>;
  setDraft: (updater: (prev: Record<string, string>) => Record<string, string>) => void;
  dirtyCount: number;
  pending: boolean;
  notice: { tone: 'ok' | 'error'; text: string } | null;
  onSave: () => void;
}) {
  const { text } = useAdminLocale();
  const categoryDirty = props.items.filter(
    (item) => (props.draft[item.key] ?? item.value) !== item.value,
  ).length;

  return (
    <div className="space-y-4">
      <SettingsStickyBar
        title={props.label}
        description={text(
          `${props.description}. Οι αλλαγές αποθηκεύονται μαζί με όλες τις παραμέτρους.`,
          `${props.description}. Changes are saved together with all settings.`,
        )}
        notice={props.notice}
        actions={
          <Button
            type="button"
            disabled={props.pending || props.dirtyCount === 0}
            onClick={props.onSave}
          >
            <Icon name="save" className="size-4" />
            {text('Αποθήκευση', 'Save')}
            {props.dirtyCount > 0 ? ` (${props.dirtyCount})` : ''}
          </Button>
        }
      />
      {categoryDirty > 0 && categoryDirty < props.dirtyCount ? (
        <p className="text-sm text-amber-800 dark:text-amber-200">
          {text(
            `Υπάρχουν ${props.dirtyCount - categoryDirty} αλλαγές και σε άλλες κατηγορίες· θα αποθηκευτούν μαζί.`,
            `There are ${props.dirtyCount - categoryDirty} changes in other categories; they will be saved together.`,
          )}
        </p>
      ) : null}
      {props.category === 'branding' ? (
        <div className="space-y-4">
          <ThemeStyleCard items={props.items} draft={props.draft} setDraft={props.setDraft} />
          <BrandFilesCard />
          {BRANDING_GROUPS.map((group) => {
            const groupItems = group.keys
              .map((key) => props.items.find((item) => item.key === key))
              .filter((item): item is ApplicationSettingItem => Boolean(item));
            const leftovers =
              group.id === 'identity'
                ? props.items.filter(
                    (item) => !BRANDING_GROUPS.some((g) => g.keys.includes(item.key)),
                  )
                : [];
            const rows = [...groupItems, ...leftovers];
            if (rows.length === 0) return null;
            return (
              <Card
                key={group.id}
                title={text(group.title, group.titleEn)}
                description={text(group.description, group.descriptionEn)}
              >
                <div className="grid gap-4 lg:grid-cols-2">
                  {rows.map((item) => (
                    <SettingField
                      key={item.key}
                      item={item}
                      value={props.draft[item.key] ?? item.value}
                      onChange={(value) =>
                        props.setDraft((prev) => {
                          const next = { ...prev };
                          if (value === item.value) delete next[item.key];
                          else next[item.key] = value;
                          return next;
                        })
                      }
                      onReset={() =>
                        props.setDraft((prev) => {
                          const next = { ...prev };
                          next[item.key] = item.defaultValue;
                          return next;
                        })
                      }
                    />
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card
          title={props.label}
          description={text(`${props.items.length} ρυθμίσεις`, `${props.items.length} settings`)}
        >
          <div className="grid gap-4 lg:grid-cols-2">
            {props.items.map((item) => {
              const optionsRaw =
                props.draft.admin_table_page_size_options ??
                props.items.find((row) => row.key === 'admin_table_page_size_options')?.value ??
                '[10,20,50,100]';
              const pageSizeOptions = parseTablePageSizeOptions(optionsRaw);
              return (
                <SettingField
                  key={item.key}
                  item={item}
                  value={props.draft[item.key] ?? item.value}
                  pageSizeOptions={pageSizeOptions}
                  onChange={(value) =>
                    props.setDraft((prev) => {
                      const next = { ...prev, [item.key]: value };
                      if (item.key === 'admin_table_page_size_options') {
                        const sizes = parseTablePageSizeOptions(value);
                        const current = Number(
                          next.admin_table_page_size ??
                            props.items.find((row) => row.key === 'admin_table_page_size')?.value ??
                            '20',
                        );
                        if (sizes.length > 0 && !sizes.includes(current)) {
                          next.admin_table_page_size = String(sizes[0]);
                        }
                      }
                      return next;
                    })
                  }
                  onReset={() =>
                    props.setDraft((prev) => ({
                      ...prev,
                      [item.key]: item.defaultValue,
                    }))
                  }
                />
              );
            })}
          </div>
        </Card>
      )}
    </div>
  );
}

function SettingsSidebar(props: {
  section: SectionId;
  onSelect: (id: SectionId) => void;
  dirtyByCategory: Record<string, number>;
}) {
  const { text } = useAdminLocale();
  const core = NAV.filter((item) => item.kind === 'core');
  const app = NAV.filter((item) => item.kind === 'app');

  function navButton(item: (typeof NAV)[number]) {
    const active = props.section === item.id;
    const dirty =
      item.kind === 'app' && item.category ? (props.dirtyByCategory[item.category] ?? 0) : 0;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => props.onSelect(item.id)}
        className={[
          'flex w-full items-start gap-3 rounded-lg px-3 py-2.5 text-left transition',
          active
            ? 'bg-[color:var(--admin-sidebar-active-bg)] text-[color:var(--admin-sidebar-active-text)]'
            : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
        ].join(' ')}
      >
        <Icon name={item.icon} className="mt-0.5 size-4 shrink-0 opacity-80" />
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="block text-sm font-medium">{text(item.label, item.labelEn)}</span>
            {dirty > 0 ? (
              <span
                className={[
                  'rounded-full px-1.5 text-[10px] font-semibold',
                  active
                    ? 'bg-white/20 text-[color:var(--admin-sidebar-active-text)]'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
                ].join(' ')}
              >
                {dirty}
              </span>
            ) : null}
          </span>
          <span
            className={[
              'mt-0.5 block text-xs',
              active
                ? 'text-[color:var(--admin-sidebar-active-text)]/75'
                : 'text-slate-500 dark:text-slate-400',
            ].join(' ')}
          >
            {text(item.description, item.descriptionEn)}
          </span>
        </span>
      </button>
    );
  }

  return (
    <>
      <nav
        aria-label={text('Κατηγορίες ρυθμίσεων', 'Settings categories')}
        className="hidden w-56 shrink-0 self-stretch overflow-y-auto lg:block xl:w-64"
      >
        <div className="space-y-5">
          <div>
            <p className="mb-2 px-3 text-[11px] font-semibold tracking-wide text-slate-500 uppercase dark:text-slate-400">
              {text('Βασικά', 'Core')}
            </p>
            <div className="space-y-1">{core.map(navButton)}</div>
          </div>
          <div>
            <p className="mb-2 px-3 text-[11px] font-semibold tracking-wide text-slate-500 uppercase dark:text-slate-400">
              {text('Παράμετροι', 'Settings')}
            </p>
            <div className="space-y-1">{app.map(navButton)}</div>
          </div>
        </div>
      </nav>

      <div className="lg:hidden">
        <div className="-mx-1 flex gap-1 overflow-x-auto px-1 pb-1">
          {NAV.map((item) => {
            const active = props.section === item.id;
            const dirty =
              item.kind === 'app' && item.category
                ? (props.dirtyByCategory[item.category] ?? 0)
                : 0;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => props.onSelect(item.id)}
                className={[
                  'shrink-0 rounded-full px-3 py-1.5 text-sm whitespace-nowrap',
                  active
                    ? 'bg-[color:var(--admin-sidebar-active-bg)] text-[color:var(--admin-sidebar-active-text)]'
                    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
                ].join(' ')}
              >
                {text(item.label, item.labelEn)}
                {dirty > 0 ? ` · ${dirty}` : ''}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}

export function PlatformSettingsPage() {
  const { text } = useAdminLocale();
  const settings = useRemote<PlatformSettings>('/v1/platform/settings', null);
  const appSettings = useRemote<{ items: ApplicationSettingItem[] }>(
    '/v1/platform/application-settings',
    null,
  );
  const [params, setParams] = useSearchParams();
  const section: SectionId = isSectionId(params.get('section'))
    ? (params.get('section') as SectionId)
    : 'provider';
  const navItem = NAV.find((item) => item.id === section) ?? NAV[0]!;

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const serverSnapshot = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!appSettings.data) return;
    const incoming: Record<string, string> = {};
    for (const item of appSettings.data.items) incoming[item.key] = item.value;
    const previous = serverSnapshot.current;
    setDraft((current) => {
      const next: Record<string, string> = {};
      for (const [key, serverValue] of Object.entries(incoming)) {
        const local = current[key];
        const untouched = local === undefined || local === previous[key];
        next[key] = untouched ? serverValue : local;
      }
      return next;
    });
    serverSnapshot.current = incoming;
  }, [appSettings.data]);

  const dirtyItems = useMemo(() => {
    if (!appSettings.data) return [];
    return appSettings.data.items.filter((item) => (draft[item.key] ?? item.value) !== item.value);
  }, [draft, appSettings.data]);

  const dirtyByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const item of dirtyItems) {
      counts[item.category] = (counts[item.category] ?? 0) + 1;
    }
    return counts;
  }, [dirtyItems]);

  const categoryItems = useMemo(() => {
    if (!appSettings.data || !navItem.category) return [];
    return appSettings.data.items.filter((item) => item.category === navItem.category);
  }, [appSettings.data, navItem.category]);

  function selectSection(id: SectionId): void {
    setParams(id === 'provider' ? {} : { section: id }, { replace: true });
  }

  async function saveApplicationSettings(): Promise<void> {
    if (dirtyItems.length === 0) return;
    setPending(true);
    setNotice(null);
    try {
      await readJson(
        '/v1/platform/application-settings',
        {
          method: 'PATCH',
          body: JSON.stringify({
            items: dirtyItems.map((item) => ({
              key: item.key,
              value: draft[item.key] ?? item.value,
            })),
          }),
        },
        null,
      );
      setNotice({
        tone: 'ok',
        text: text(
          `Αποθηκεύτηκαν ${dirtyItems.length} ρυθμίσεις.`,
          `${dirtyItems.length} settings were saved.`,
        ),
      });
      await appSettings.mutate();
      refreshAdminUiPrefs();
    } catch (error) {
      setNotice({
        tone: 'error',
        text:
          error instanceof ApiError
            ? error.message
            : text('Η αποθήκευση απέτυχε.', 'Saving failed.'),
      });
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="flex min-h-0 flex-col gap-4 lg:h-[calc(100dvh-6.5rem)] lg:overflow-hidden">
      <PageHeader
        description={text(
          'Κοινές ρυθμίσεις για όλους τους πελάτες. Κάθε πελάτης έχει δικές του ρυθμίσεις στην ενότητά του.',
          'Shared settings for all customers. Each customer has separate settings in their own section.',
        )}
      />
      {settings.error ? (
        <p className="text-sm text-rose-700 dark:text-rose-300">{settings.error.message}</p>
      ) : null}
      {!settings.data && !settings.error ? (
        <p className="text-sm text-slate-500 dark:text-slate-400">{text('Φόρτωση…', 'Loading…')}</p>
      ) : null}
      {settings.data ? (
        <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:gap-6 lg:overflow-hidden">
          <SettingsSidebar
            section={section}
            onSelect={selectSection}
            dirtyByCategory={dirtyByCategory}
          />
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain pr-1">
            {section === 'provider' ? (
              <ProviderForm
                key={settings.data.provider.kind}
                settings={settings.data}
                onSaved={() => void settings.mutate()}
              />
            ) : null}
            {section === 'smtp' ? (
              <SmtpForm
                key={`${settings.data.smtp.source}-${settings.data.smtp.host ?? ''}-${settings.data.smtp.passwordSet}`}
                settings={settings.data}
                onSaved={() => void settings.mutate()}
              />
            ) : null}
            {section === 'mail' ? <EmailTemplatesPanel /> : null}
            {section !== 'mail' && navItem.kind === 'app' ? (
              appSettings.error ? (
                <p className="text-sm text-rose-700 dark:text-rose-300">
                  {appSettings.error.message}
                </p>
              ) : !appSettings.data ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {text('Φόρτωση παραμέτρων…', 'Loading settings…')}
                </p>
              ) : (
                <ApplicationCategoryPanel
                  category={navItem.category!}
                  label={text(navItem.label, navItem.labelEn)}
                  description={text(navItem.description, navItem.descriptionEn)}
                  items={categoryItems}
                  draft={draft}
                  setDraft={setDraft}
                  dirtyCount={dirtyItems.length}
                  pending={pending}
                  notice={notice}
                  onSave={() => void saveApplicationSettings()}
                />
              )
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
